import React from 'react';
import { Link } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import { useLegalSettings } from '../hooks/useLegalSettings';

const LAST_UPDATED = 'December 2025';

export default function RefundPage() {
  const { business_name, abn, contact_email } = useLegalSettings();
  return (
    <div className="max-w-3xl mx-auto fade-in" data-testid="refund-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
          <RotateCcw className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-text">Refund Policy</h1>
          <p className="text-text-muted text-sm">Last updated: {LAST_UPDATED}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8 space-y-6 text-text leading-relaxed">
        <section>
          <p>
            <strong>{business_name}</strong> (ABN <strong>{abn}</strong>) is an Australian sole trader. This Refund Policy sets out when you can get a refund for the Digital Safety Course. It operates alongside your rights under the <strong>Australian Consumer Law</strong> — nothing in this policy limits those rights.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">Your rights under the Australian Consumer Law</h2>
          <p>
            Under the Australian Consumer Law, our Course comes with guarantees that cannot be excluded. In particular, it must be:
          </p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li><strong>provided with due care and skill</strong></li>
            <li><strong>fit for the purpose we described</strong> (teaching general digital safety skills)</li>
            <li><strong>delivered within a reasonable time</strong></li>
          </ul>
          <p className="mt-2">
            If we fail to meet one of these guarantees, you are entitled to a <strong>remedy</strong>. For a major problem you can choose a refund. For a minor problem we can choose to fix the problem (for example, re-grant access or correct an error) within a reasonable time.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">14-day satisfaction guarantee</h2>
          <p>
            In addition to your consumer-law rights, we offer a goodwill <strong>14-day refund</strong> on the Course. If within 14 days of purchase you decide the Course isn't right for you, email us at <a href={`mailto:${contact_email}`} className="text-primary font-semibold hover:underline">{contact_email}</a> and we'll refund the amount you paid in full. Access to the paid modules will be removed; your free Module 1 preview remains.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">When a refund may not apply</h2>
          <p>Outside the 14-day period, we may not provide a refund where:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>you have already completed all 12 modules and received your certificate, and</li>
            <li>the Course has been delivered as described without a defect.</li>
          </ul>
          <p className="mt-2">
            If you have a problem outside the 14-day window, please still contact us — we handle every case individually and will always meet our obligations under the Australian Consumer Law.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">How to request a refund</h2>
          <ol className="list-decimal pl-6 space-y-1 mt-2">
            <li>Email us at <a href={`mailto:${contact_email}`} className="text-primary font-semibold hover:underline">{contact_email}</a> with your order details (the email you paid with and the approximate date).</li>
            <li>Tell us briefly what the issue is, so we can improve the Course.</li>
            <li>We'll reply within 2 business days.</li>
            <li>Approved refunds are issued to the original payment method via Stripe and typically appear in 5–10 business days, depending on your bank.</li>
          </ol>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">Payment processor</h2>
          <p>
            All refunds are processed through Stripe to the original card or payment method used at checkout. We do not hold your card details ourselves.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">Chargebacks</h2>
          <p>
            Before raising a chargeback with your bank, please contact us so we can try to resolve the issue directly. In most cases we can refund you faster than a bank-initiated dispute.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">Contact</h2>
          <p>
            Refund questions? Contact us at <a href={`mailto:${contact_email}`} className="text-primary font-semibold hover:underline">{contact_email}</a>.
          </p>
          <p className="mt-3 text-sm text-text-muted">
            See also our <Link to="/terms" className="text-primary font-semibold hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-primary font-semibold hover:underline">Privacy Policy</Link>.
          </p>
        </section>
      </div>
    </div>
  );
}
